import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button, CircularProgress, Container, Pagination, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { EStatusCode } from '../../api/enum';
import { IPaginatedResponse } from '../../api/httpClient';
import { IGetProductPreviewResponse } from '../../api/product/iApiResponse';
import * as ProductHelper from '../../api/product/productHelper';
import { route } from '../../route/Index';
import { EStatus } from '../../shared/enum';
import isAdmin from '../../shared/hoc/isAdmin';
import { IProduct } from '../../shared/iProject';
import { toastAlert } from '../../shared/util';

interface IProductPanelProps {}

const ProductPanelContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const ProductPanel = (props: IProductPanelProps) => {
    let pageSize = 4;

    let timer = React.useRef<NodeJS.Timeout | null>(null);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [productPreview, setProductPreview] = React.useState<IPaginatedResponse<IGetProductPreviewResponse>>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);
    const [filterName, setFilterName] = React.useState<string | null>(null);

    const fetchProductPreview = async (page: number, filter?: string) => {
        setIsLoading(true);
        let res = await ProductHelper.getPaginatedProductPreviewsAsync(page, pageSize, filter);
        setProductPreview(res.data);
        setIsLoading(false);
    };

    React.useEffect(() => {
        fetchProductPreview(pageNumber);
    }, [pageNumber]);

    const handleGoToProduct = (id: number) => navigate(route.backofficeProduct.replace(':param', id.toString()));

    const handleChangeStatus = async (toTest: boolean, id: number) => {
        let product = await ProductHelper.getProductByIdAsync(id);
        let payload: IProduct = {
            ...product.data!,
            status: toTest ? EStatus.Test : EStatus.Deleted,
        };
        let res = await ProductHelper.UpdateProductDtoAsync(payload);
        if (res.status == EStatusCode.Ok) {
            fetchProductPreview(pageNumber);
            toastAlert('success', t('updatedProduct'));
        }
    };

    React.useEffect(() => {
        if (filterName !== null) {
            if (timer.current) {
                clearTimeout(timer.current);
            }
            timer.current = setTimeout(() => {
                fetchProductPreview(1, filterName);
            }, 1000);
        }
    }, [filterName]);

    return (
        <ProductPanelContainer maxWidth='md'>
            <Stack height='100%' direction='column' spacing={4}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h1' gutterBottom>
                        {t('products')}
                    </Typography>
                    <Button variant='orange' endIcon={<AddCircleIcon />} onClick={() => navigate(route.backofficeProduct.replace(':param', 'newProduct'))}>
                        {t('addNewProduct')}
                    </Button>
                </Stack>
                <TextField
                    id='input-search'
                    placeholder={t('searchName')}
                    InputProps={{
                        type: 'search',
                    }}
                    style={{ marginTop: '25px' }}
                    onChange={e => setFilterName(e.target.value)}
                />
                {isLoading && <CircularProgress />}
                {!isLoading && productPreview?.value?.length! > 0 && (
                    <>
                        <Stack flexGrow={1}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} size='small' aria-label='simple table'>
                                    <TableHead variant='greenLightWithoutBorder'>
                                        <TableRow>
                                            <TableCell>{t('name')}</TableCell>
                                            <TableCell align='center'>{t('lastUpdate')}</TableCell>
                                            <TableCell align='right'>{t('enabled')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {productPreview!.value.map(x => (
                                            <TableRow key={x.name} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell onClick={() => handleGoToProduct(x.id)} component='th' scope='row'>
                                                    {x.name}
                                                </TableCell>
                                                <TableCell onClick={() => handleGoToProduct(x.id)} align='center'>
                                                    {format(x.lastUpdateDate, 'dd MMMM yyyy')}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    <Switch
                                                        onChange={e => handleChangeStatus(e.target.checked, x.id)}
                                                        aria-label={`switch ${x.name}`}
                                                        checked={x.status == EStatus.Public || x.status == EStatus.Test}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                        {productPreview!.pageCount > 1 && (
                            <Stack alignItems='end'>
                                <Pagination
                                    showFirstButton
                                    showLastButton
                                    variant='outlined'
                                    shape='rounded'
                                    page={productPreview?.pageNumber}
                                    count={productPreview?.pageCount}
                                    onChange={(e, value) => setpageNumber(value)}
                                />
                            </Stack>
                        )}
                    </>
                )}
                {!isLoading && productPreview?.value?.length! == 0 && <div>Empty</div>}
            </Stack>
        </ProductPanelContainer>
    );
};

export default isAdmin(ProductPanel);
